import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, AlertTriangle, Smartphone, Copy, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';


/**
 * MFA Enforcement Gate
 * 
 * This component checks if the user's organization requires MFA.
 * If MFA is required but not enabled, it forces the user to set it up
 * before they can access the rest of the application.
 */
export default function MFAEnforcementGate({ children }) {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [checking, setChecking] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);
  const [enforcementMessage, setEnforcementMessage] = useState('');
  
  // MFA Setup state
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [setupStep, setSetupStep] = useState('info'); // 'info' | 'setup' | 'verify'

  useEffect(() => {
    if (token && user) {
      checkMFAEnforcement();
    } else {
      setChecking(false);
    }
  }, [token, user]);

  const checkMFAEnforcement = async () => {
    try {
      const response = await api.get(`/auth/mfa/enforcement`);
      const { setup_required, message } = response.data;
      
      setSetupRequired(setup_required);
      setEnforcementMessage(message || '');
      
      if (setup_required) {
        setSetupStep('info');
      }
    } catch (error) {
      console.error('Failed to check MFA enforcement:', error);
      // Don't block on error - let user continue
      setSetupRequired(false);
    } finally {
      setChecking(false);
    }
  };

  const handleStartSetup = async () => {
    try {
      const response = await api.post(`/auth/mfa/setup`);
      setMfaSetupData(response.data);
      setSetupStep('setup');
    } catch (error) {
      toast.error('Failed to start MFA setup');
    }
  };

  const handleVerifyMFA = async () => {
    if (verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setVerifying(true);
    try {
      await api.post(`/auth/mfa/verify`, { code: verifyCode });
      toast.success('Two-factor authentication enabled successfully!');
      setSetupRequired(false);
      setSetupStep('info');
      setMfaSetupData(null);
      setVerifyCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifying(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Show loading while checking
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Checking security requirements...</span>
        </div>
      </div>
    );
  }

  // If MFA setup is required, show the mandatory setup dialog
  if (setupRequired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md">
          <div className="bg-card border border-border rounded-xl shadow-lg overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 p-6 border-b border-border">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">Security Required</h2>
                  <p className="text-sm text-muted-foreground">Two-Factor Authentication Setup</p>
                </div>
              </div>
              
              <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  {enforcementMessage}
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {setupStep === 'info' && (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Two-factor authentication (2FA) adds an extra layer of security to your account 
                    by requiring a code from your authenticator app in addition to your password.
                  </p>
                  
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground">You'll need:</p>
                    <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                      <li>An authenticator app (Google Authenticator, Authy, etc.)</li>
                      <li>Your phone to scan the QR code</li>
                    </ul>
                  </div>
                  
                  <Button 
                    onClick={handleStartSetup}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    <Smartphone className="w-4 h-4 mr-2" />
                    Set Up Two-Factor Authentication
                  </Button>
                </div>
              )}

              {setupStep === 'setup' && mfaSetupData && (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground text-center">
                    Scan this QR code with your authenticator app
                  </p>
                  
                  {/* QR Code */}
                  <div className="flex justify-center">
                    <div className="p-4 bg-white rounded-lg shadow-sm">
                      <img 
                        src={mfaSetupData.qr_code} 
                        alt="MFA QR Code" 
                        className="w-48 h-48"
                      />
                    </div>
                  </div>
                  
                  {/* Manual Entry Key */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">
                      Or enter this key manually:
                    </Label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-2 bg-secondary rounded text-xs font-mono break-all">
                        {mfaSetupData.manual_entry_key}
                      </code>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => copyToClipboard(mfaSetupData.manual_entry_key)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={() => setSetupStep('verify')}
                    className="w-full"
                  >
                    I've scanned the code
                  </Button>
                </div>
              )}

              {setupStep === 'verify' && (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground text-center">
                    Enter the 6-digit code from your authenticator app
                  </p>
                  
                  <Input
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-[0.5em] font-mono h-14"
                    maxLength={6}
                    autoFocus
                  />
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      onClick={() => setSetupStep('setup')}
                      className="flex-1"
                    >
                      Back
                    </Button>
                    <Button 
                      onClick={handleVerifyMFA}
                      disabled={verifyCode.length !== 6 || verifying}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    >
                      {verifying ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                      )}
                      Verify & Enable
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No enforcement needed, render children
  return children;
}
